// Display helpers for the menu pages. Copy is Italian, like the rest of the portal.
import { httpStatusOf } from "@/core/http/HttpError";
import { parseMoneyMinor } from "@/utils/currency";
import type { DeployStatus, MenuItem, MenuSummary, PricePoint } from "./types";

/** "1 modifica" / "3 modifiche". */
export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Cents as the menu writes euros: 700 -> "7", 750 -> "7,50", 123400 -> "1.234". */
export function euros(minor: number): string {
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(minor / 100);
}

/** A price as the drawer's input shows it: 750 -> "7,50", null -> "". */
export function priceInput(minor: number | null): string {
  return minor === null ? "" : (minor / 100).toFixed(2).replace(".", ",");
}

export type ParsedPrice = { ok: true; amountMinor: number | null } | { ok: false };

/**
 * What the drawer's price input holds, in cents: "7,50", "7.5" and "€ 7" read as euros, an empty input as no price.
 * Anything else, a negative amount included, is not a price, so a typo never saves as a cleared one.
 */
export function parsePrice(text: string): ParsedPrice {
  if (text.trim() === "") return { ok: true, amountMinor: null };
  const minor = parseMoneyMinor(text);
  return minor === null || minor < 0 ? { ok: false } : { ok: true, amountMinor: minor };
}

function pricePoint(p: PricePoint & { amountMinor: number }): string {
  const value = euros(p.amountMinor);
  const label = p.label.trim();
  if (p.unit !== "€") return `${label ? `${label} ` : ""}${value} ${p.unit}`;
  return label ? `${label} ${value}` : `€ ${value}`;
}

/** "Calice 7 · Bottiglia 32", "€ 11", cheapest first, whatever order they were entered in. Empty with no price. */
export function pricesLine(item: Pick<MenuItem, "prices" | "priceHidden">): string {
  if (item.priceHidden) return "Prezzo non mostrato";
  return item.prices
    .filter((p): p is PricePoint & { amountMinor: number } => p.amountMinor !== null)
    .sort((a, b) => a.amountMinor - b.amountMinor)
    .map(pricePoint)
    .join(" · ");
}

/** "producer · region" for a wine, else empty. */
export function wineLine(item: Pick<MenuItem, "wine">): string {
  const w = item.wine;
  if (!w) return "";
  return [w.producer, w.region].map((s) => s.trim()).filter(Boolean).join(" · ");
}

export type Tone = "muted" | "strong" | "ok" | "error";
export interface StatusLine {
  text: string;
  tone: Tone;
}

/** Each tone's text colour. */
export const TONE_CLASS: Record<Tone, string> = {
  muted: "text-gray-500",
  strong: "text-gray-700",
  ok: "text-emerald-700",
  error: "text-red-700",
};

export const SITE_FAILED = "Aggiornamento del sito non riuscito: riprova o contatta l'agenzia";

/** The website build after a publish, in words. A skipped build (no trigger set) says nothing. */
export function siteStatus(status: DeployStatus | null | undefined, short = false): StatusLine | null {
  switch (status) {
    case "running":
      return { text: "Sito in aggiornamento…", tone: "muted" };
    case "succeeded":
      return { text: "Sito aggiornato", tone: "ok" };
    case "failed":
      return { text: short ? "Aggiornamento del sito non riuscito" : SITE_FAILED, tone: "error" };
    default:
      return null;
  }
}

/** A menus-list row's second line: its publish state, then its website's, when that says anything. */
export function menuStatus(menu: Pick<MenuSummary, "pending" | "publishedVersionId" | "latestVersion">): StatusLine[] {
  const publish: StatusLine = menu.pending > 0
    ? { text: `${plural(menu.pending, "modifica", "modifiche")} da pubblicare`, tone: "strong" }
    : { text: menu.publishedVersionId ? "Pubblicato" : "Non ancora pubblicato", tone: "muted" };
  const site = siteStatus(menu.latestVersion?.deployStatus, true);
  return site ? [publish, site] : [publish];
}

export interface PublishBarState {
  /** "3 modifiche da pubblicare", or null when only the site's status is left to say. */
  message: string | null;
  site: StatusLine | null;
  /** The button: Pubblica while there are changes, Riprova after a failed build, none otherwise. */
  action: "Pubblica" | "Riprova" | null;
}

/**
 * The bar pinned under the menu: there while the working copy has unpublished changes, or while the last publish's
 * website build is running or has just finished. A session that only reads never sees it, since it cannot publish.
 */
export function publishBar(input: { pending: number; canEdit: boolean; deploy: DeployStatus | null }): PublishBarState | null {
  if (!input.canEdit) return null;
  const site = siteStatus(input.deploy);
  if (input.pending <= 0 && site === null) return null;
  return {
    message: input.pending > 0 ? `${plural(input.pending, "modifica", "modifiche")} da pubblicare` : null,
    site,
    action: input.pending > 0 ? "Pubblica" : input.deploy === "failed" ? "Riprova" : null,
  };
}

/**
 * Why an edit did not save, shown by the field. A refused value (400) carries the server's reason, which names the
 * field's rule; a session that only reads (403) and a lost connection get their own line.
 */
export function saveError(error: unknown): string {
  const status = httpStatusOf(error);
  if (status === 400 && error instanceof Error) return `Non salvato: ${error.message}`;
  if (status === 403) return "Non salvato: puoi solo consultare il menu.";
  if (status === null) return "Non salvato: controlla la connessione e riprova.";
  return "Non salvato. Riprova.";
}
